import api from "./api"
import { Credentials, User } from "../Interfaces"
import { AxiosError } from "axios"

function getAxiosErrorMessage(error: AxiosError): string {
  const message = (error.response?.data as { message?: unknown } | undefined)?.message
  if (typeof message === "string" && message) {
    return message
  }
  if (Array.isArray(message)) {
    const joined = message.filter((item): item is string => typeof item === "string").join(", ")
    if (joined) {
      return joined
    }
  }
  return error.response?.statusText || "Request failed"
}

class AuthService {
  private baseUrl = "/auth";

  async getAuthFromCredentials(credentials: Credentials) {
    try {
      const response = await api.post(
        `${this.baseUrl}/login`,
        credentials
      )
      await this.setToken(response.data.accessToken, response.data.user)
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(getAxiosErrorMessage(error))
      } else {
        console.error(error);
      }
    }
  }

  // get authenticated user
  async getAthenticatedUser() {
    const response = await api.get("/authenticate")
    return response.data
  }

  async forgotPassword(email: string) {
    try {
      const response = await api.post(
        `${this.baseUrl}/forgot-password`,
        { email }
      )
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(getAxiosErrorMessage(error))
      } else {
        console.error(error);
      }
    }
  }

  async requestAccountDeletion(email: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`${this.baseUrl}/request-account-deletion`, { email })
    return response.data
  }

  async confirmAccountDeletion(token: string): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`${this.baseUrl}/confirm-account-deletion`, { token })
    return response.data
  }

  async resetPassword(token: string, password: string) {
    try {
      const response = await api.post(
        `${this.baseUrl}/reset-password`,
        { token, password }
      )
      return response.data
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response);
        throw Error(getAxiosErrorMessage(error))
      } else {
        console.error(error);
      }
    }
  }

  private async setToken(token: string, user: User) {
    localStorage.setItem("token", token)
    localStorage.setItem("user", JSON.stringify(user))
  }

  logout() {
    localStorage.removeItem("token")
    localStorage.removeItem("user")
  }
}

export const authService = new AuthService()
